import { useMemo } from "react";
import type { FrameProps } from "@/components/shapes/shared/frame-props";
import { Icons } from "@/components/ui/icons";
import { copyFrameLink } from "@/lib/frame-menu-actions";
import { openExternalUrl } from "@/lib/open-external-url";
import {
  useFrameChromeConfig,
  type FrameChromeConfig,
} from "../shared/frame-chrome-config";
import { cardPaint } from "../shared/card-paint";
import { FrameEmptyState } from "../shared/frame-empty-state";
import { ObjectFrame } from "../shared/object-frame";
import { LinkCover } from "./link-cover";
import { domainFromUrl, linksFrom } from "./link-preview-links";
import { useT } from "@/i18n";
import { linkPreviewFrameMessages } from "@/i18n/messages/frames-link-preview";

/**
 * A link: its card's cover, larger, then every link as a row (title,
 * description, domain) that opens it. An opened window's pill opens the
 * first link and its ⋯ copies it.
 */
export function LinkPreviewFrame({ id, data }: FrameProps) {
  const t = useT(linkPreviewFrameMessages);
  const links = linksFrom(data.content ?? {});
  const first = links[0];
  const firstUrl = first?.url;
  const chromeConfig = useMemo<FrameChromeConfig>(
    () =>
      firstUrl
        ? {
            tools: [
              {
                kind: "button",
                id: "open-link",
                label: t("openLink"),
                icon: Icons.ArrowUpRight,
                onSelect: () => void openExternalUrl(firstUrl),
              },
            ],
            menuItems: [
              {
                id: "copy-link",
                label: t("copyLink"),
                icon: Icons.Link,
                onSelect: () => void copyFrameLink(id, firstUrl),
              },
            ],
          }
        : { tools: [] },
    [firstUrl, id, t],
  );
  useFrameChromeConfig(chromeConfig);
  return (
    <ObjectFrame id={id} paint={cardPaint(data)} testId="link-preview-frame">
      {!first ? (
        <FrameEmptyState
          frameId={id}
          status={data.status}
          testId="link-preview-frame-empty"
        >
          {t("noPreview")}
        </FrameEmptyState>
      ) : (
        <>
          <LinkCover link={first} density="regular" />
          <div
            className="flex shrink-0 flex-col py-[0.3em]"
            data-testid="link-preview-frame-list"
          >
            {links.map((link, index) => {
              const domain = domainFromUrl(link.url);
              return (
                <a
                  key={`${link.url}-${index}`}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`link-preview-frame-link-${index}`}
                  className="nodrag mx-[0.6em] block min-w-0 rounded-[0.8em] px-[1em] py-[0.25em] leading-[1.32] hover:bg-[var(--color-interactive-hover)]"
                >
                  <span className="block truncate font-display font-medium text-card-ink">
                    {link.title || link.siteName || domain}
                  </span>
                  {link.description && (
                    <span className="block truncate text-[0.84em] text-card-body">
                      {link.description}
                    </span>
                  )}
                  <span className="flex min-w-0 items-center gap-[0.3em] text-[0.84em] text-card-muted">
                    <span className="truncate">{domain}</span>
                    <Icons.ArrowUpRight
                      className="h-[1em] w-[1em] shrink-0"
                      aria-hidden="true"
                    />
                  </span>
                </a>
              );
            })}
          </div>
        </>
      )}
    </ObjectFrame>
  );
}
